import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import EmailVerifyBanner from '@/components/EmailVerifyBanner';
import { CalendarBlank, CurrencyDollar, Star, ChartBar, Check, X, Clock, User, CaretLeft, CaretRight, IdentificationCard, ShieldCheck, UploadSimple, WarningCircle, Eye, Wallet, ShareNetwork, Copy, LinkSimple, EnvelopeSimple, Phone } from '@phosphor-icons/react';
import { toast } from '@/components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Vendor's incoming quote requests: respond with a custom price or decline
function QuotesPanel({ quotes, headers, onChanged }) {
  const [respondTo, setRespondTo] = useState(null);
  const [form, setForm] = useState({ price: '', deposit_pct: '', terms: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputCls = "w-full h-10 rounded-xl border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40";

  const send = async (quoteId) => {
    setBusy(true); setError('');
    try {
      await axios.put(`${API}/quotes/${quoteId}/respond`, {
        price: parseFloat(form.price),
        deposit_pct: form.deposit_pct === '' ? null : parseInt(form.deposit_pct, 10),
        terms: form.terms, message: form.message,
      }, { headers });
      setRespondTo(null);
      setForm({ price: '', deposit_pct: '', terms: '', message: '' });
      onChanged();
    } catch (err) { setError(err?.response?.data?.detail || 'Could not send the quote.'); }
    setBusy(false);
  };
  const decline = async (quoteId) => {
    try { await axios.put(`${API}/quotes/${quoteId}/decline`, {}, { headers }); onChanged(); }
    catch (err) { console.error('Decline failed:', err); }
  };

  if (quotes.length === 0) {
    return (
      <div className="flex flex-col items-center text-center py-16 bg-card rounded-2xl border border-border/70 shadow-soft fade-up" data-testid="quotes-empty">
        <div className="bg-secondary rounded-full p-4 mb-4"><CurrencyDollar size={28} className="text-primary" /></div>
        <p className="text-sm text-muted-foreground">No quote requests yet. Customers can ask for a custom price from your profile.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="provider-quotes-list">
      {quotes.map(q => (
        <div key={q.quote_id} className="bg-card border border-border/70 rounded-xl shadow-soft p-5 fade-up" data-testid={`pq-${q.quote_id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-semibold text-foreground">{q.customer_name || 'Customer'}</p>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide ${
                  q.status === 'requested' ? 'bg-amber-500/10 text-amber-600' :
                  q.status === 'quoted' ? 'bg-primary/10 text-primary' :
                  q.status === 'accepted' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-secondary text-muted-foreground'}`}>
                  {q.status === 'requested' ? 'Needs reply' : q.status}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{q.event_type}{q.event_date ? ` · ${q.event_date}` : ''}{q.guest_count ? ` · ${q.guest_count} guests` : ''}{q.budget ? ` · budget ${q.budget}` : ''}</p>
              {q.message && <p className="text-xs text-foreground/80 mt-2 bg-secondary/50 rounded-lg px-3 py-2">“{q.message}”</p>}
            </div>
            <div className="text-right shrink-0">
              {q.status === 'requested' && respondTo !== q.quote_id && (
                <div className="flex gap-2">
                  <button onClick={() => setRespondTo(q.quote_id)} className="bg-primary text-primary-foreground rounded-xl px-4 h-10 text-xs font-semibold btn-anim shadow-soft hover:bg-primary/90 cursor-pointer" data-testid={`respond-${q.quote_id}`}>Send quote</button>
                  <button onClick={() => decline(q.quote_id)} className="border border-border rounded-xl px-3 h-10 text-xs font-medium text-muted-foreground hover:bg-secondary/60 btn-anim cursor-pointer">Decline</button>
                </div>
              )}
              {q.status === 'quoted' && <p className="text-lg font-display font-semibold text-foreground tabular-nums">${Number(q.price).toLocaleString()}</p>}
              {q.status === 'accepted' && <p className="text-xs text-emerald-600 font-medium inline-flex items-center gap-1"><Check size={12} weight="bold" /> Booked ${Number(q.price).toLocaleString()}</p>}
            </div>
          </div>

          {respondTo === q.quote_id && (
            <div className="mt-4 pt-4 border-t border-border/60" data-testid={`respond-form-${q.quote_id}`}>
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">Your price (CAD)</label>
                  <input type="number" min="1" step="0.01" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className={inputCls} data-testid="quote-price-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1">Deposit</label>
                  <select value={form.deposit_pct} onChange={e => setForm(f => ({ ...f, deposit_pct: e.target.value }))} className={`${inputCls} cursor-pointer`}>
                    <option value="">Use my default</option>
                    <option value="0">Full payment upfront</option>
                    <option value="25">25% deposit</option>
                    <option value="50">50% deposit</option>
                  </select>
                </div>
              </div>
              <label className="block text-xs font-medium text-foreground mb-1">What's included (terms)</label>
              <input type="text" value={form.terms} onChange={e => setForm(f => ({ ...f, terms: e.target.value }))} placeholder="e.g. 6 hours, setup and teardown included" className={`${inputCls} mb-3`} data-testid="quote-terms-input" />
              <label className="block text-xs font-medium text-foreground mb-1">Message to the customer</label>
              <textarea rows={2} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none mb-3" data-testid="quote-msg-input" />
              {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
              <div className="flex gap-2 justify-end">
                <button onClick={() => setRespondTo(null)} className="h-10 px-4 rounded-xl border border-border text-xs font-medium text-muted-foreground hover:bg-secondary/60 btn-anim cursor-pointer">Cancel</button>
                <button onClick={() => send(q.quote_id)} disabled={busy || !form.price}
                  className="h-10 px-5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold btn-anim shadow-soft hover:bg-primary/90 disabled:opacity-50 cursor-pointer" data-testid="quote-send-button">
                  {busy ? 'Sending…' : 'Send quote'}
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ProviderDashboard() {
  const { user, token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [tab, setTab] = useState('bookings');
  const [loading, setLoading] = useState(true);
  const [calMonth, setCalMonth] = useState(new Date());
  const [avail, setAvail] = useState({ slots: [], booked_dates: [] });
  const [selectedDate, setSelectedDate] = useState(null);
  const [slotStart, setSlotStart] = useState('09:00');
  const [slotEnd, setSlotEnd] = useState('17:00');
  const [verifications, setVerifications] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [featuredBusy, setFeaturedBusy] = useState(false);
  const [hasAvail, setHasAvail] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [payoutsBusy, setPayoutsBusy] = useState(false);
  const [quotes, setQuotes] = useState([]);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [profRes, bookRes] = await Promise.all([
        axios.get(`${API}/providers/profile/me`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/bookings`, { headers })
      ]);
      setProfile(profRes.data);
      setBookings(bookRes.data);
      axios.get(`${API}/quotes`, { headers }).then(r => setQuotes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    } catch (err) { console.error("Provider data error:", err); } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Setup checklist: has this vendor ever set availability?
  useEffect(() => {
    axios.get(`${API}/availability/summary/me`, { headers })
      .then(r => setHasAvail(r.data.has_any))
      .catch(() => setHasAvail(false));
  }, [headers, tab]);

  // Payouts: connection status, plus refresh when returning from Stripe onboarding
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('payouts')) {
      window.history.replaceState({}, '', '/provider/dashboard');
    }
    axios.get(`${API}/providers/payouts/status`, { headers })
      .then(r => setPayouts(r.data))
      .catch(() => setPayouts({ connected: false, payouts_enabled: false }));
  }, [headers]);

  const setupPayouts = async () => {
    setPayoutsBusy(true);
    try {
      const r = await axios.post(`${API}/providers/payouts/onboard`, { origin_url: window.location.origin }, { headers });
      if (r.data.url) { window.location.href = r.data.url; return; }
    } catch (err) {
      console.error('Payout onboarding failed:', err);
      toast.error(err?.response?.data?.detail || 'Could not start payout setup. Please try again later.');
    }
    setPayoutsBusy(false);
  };

  // Returning from the Featured checkout: confirm payment, then refresh the profile
  useEffect(() => {
    const fs = new URLSearchParams(window.location.search).get('featured_session');
    if (fs && token) {
      axios.get(`${API}/providers/featured/status/${fs}`, { headers })
        .then(() => { window.history.replaceState({}, '', '/provider/dashboard'); fetchData(); })
        .catch(err => console.error('Featured activation failed:', err));
    }
  }, [token, headers, fetchData]);

  const buyFeatured = async () => {
    setFeaturedBusy(true);
    try {
      const r = await axios.post(`${API}/providers/featured/checkout`, { origin_url: window.location.origin }, { headers });
      if (r.data.url) window.location.href = r.data.url;
      else setFeaturedBusy(false);
    } catch (err) { console.error('Featured checkout failed:', err); toast.error(err?.response?.data?.detail || 'Could not start checkout.'); setFeaturedBusy(false); }
  };

  const cancelFeatured = async () => {
    if (!window.confirm('Cancel Featured Placement? You’ll stay featured until the end of your paid period, then it won’t renew.')) return;
    setFeaturedBusy(true);
    try {
      await axios.post(`${API}/providers/featured/cancel`, {}, { headers });
      toast.success('Featured Placement cancelled — it won’t renew.');
      fetchData();
    } catch (err) { console.error('Featured cancel failed:', err); toast.error(err?.response?.data?.detail || 'Could not cancel. Please try again.'); }
    finally { setFeaturedBusy(false); }
  };

  const fetchAvailability = useCallback(async () => {
    if (!user) return;
    const m = `${calMonth.getFullYear()}-${String(calMonth.getMonth() + 1).padStart(2, '0')}`;
    try {
      const r = await axios.get(`${API}/availability/${user.user_id}?month=${m}`, { headers });
      setAvail(r.data);
    } catch (err) { console.error("Availability error:", err); }
  }, [user, calMonth, headers]);

  useEffect(() => { if (tab === 'availability') fetchAvailability(); }, [tab, calMonth, fetchAvailability]);

  useEffect(() => {
    if (tab === 'verification') {
      axios.get(`${API}/verification/status`, { headers }).then(r => setVerifications(r.data)).catch(err => console.error('Verification status error:', err));
    }
  }, [tab, headers]);

  const toggleDay = async (dateStr) => {
    const existing = avail.slots.find(s => s.date === dateStr);
    if (existing) {
      await axios.delete(`${API}/availability/${dateStr}`, { headers });
    } else {
      await axios.post(`${API}/availability`, { date: dateStr, available: true, start_time: slotStart, end_time: slotEnd }, { headers });
    }
    fetchAvailability();
  };

  const blockDay = async (dateStr) => {
    await axios.post(`${API}/availability`, { date: dateStr, available: false, start_time: '00:00', end_time: '00:00', note: 'Blocked' }, { headers });
    fetchAvailability();
  };

  const updateStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const totalEarnings = bookings.filter(b => b.status === 'completed').reduce((sum, b) => sum + (b.provider_payout || 0), 0);
  const pendingBookings = bookings.filter(b => b.status === 'pending');
  const confirmedBookings = bookings.filter(b => b.status === 'confirmed');
  const completedBookings = bookings.filter(b => b.status === 'completed');
  // Money that's booked-and-paid but not yet released to the vendor — the number
  // vendors care about most ("what's coming to me"). Confirmed bookings are paid
  // into escrow and pay out on completion.
  const upcomingPayout = confirmedBookings.reduce((sum, b) => sum + (b.provider_payout || 0), 0);
  const profileViews = profile?.profile_views || 0;

  // Public profile link the vendor can share to bring in their own traffic.
  const publicUrl = profile?.profile_id ? `${window.location.origin}/provider/${profile.profile_id}` : '';
  const shareText = `Book ${profile?.business_name || 'us'} on Bookr — secure booking, protected payments.`;
  const reviewText = `Thanks again! If you have a sec, I'd really appreciate a quick review on my Bookr page: ${publicUrl}`;

  const copyLink = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label || 'Link copied');
    } catch {
      toast.error('Could not copy — long-press the link to copy it manually.');
    }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'My Bookr page', text: shareText, url: publicUrl }); }
      catch { /* user dismissed the share sheet */ }
    } else {
      copyLink(publicUrl, 'Link copied — paste it anywhere');
    }
  };

  const getStatusColor = (status) => {
    const colors = { pending: 'bg-amber-500/10 text-amber-600', confirmed: 'bg-secondary text-primary', completed: 'bg-emerald-500/10 text-emerald-600', cancelled: 'bg-red-500/10 text-red-600' };
    return colors[status] || 'bg-secondary text-muted-foreground';
  };

  if (!profile && !loading) {
    return (
      <div className="min-h-screen bg-background"><Navbar />
        <div className="max-w-lg mx-auto px-6 py-24 text-center">
          <h1 className="text-3xl font-display font-semibold tracking-tight text-foreground mb-4">Complete Your Provider Profile</h1>
          <p className="text-muted-foreground leading-relaxed mb-6">Set up your profile to start receiving bookings.</p>
          <Link to="/provider/setup-profile" className="inline-flex items-center justify-center bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90" data-testid="setup-profile-link">Set Up Profile</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="provider-dashboard">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <EmailVerifyBanner />

        {/* Setup checklist — shown until every step is done */}
        {profile && (() => {
          const steps = [
            { key: 'email', label: 'Verify your email', done: user?.email_verified !== false,
              hint: 'Use the banner above to resend the link' },
            { key: 'photos', label: 'Add portfolio photos', done: (profile.portfolio_images || []).length > 0,
              hint: 'Profiles with photos get booked far more often', action: () => {}, link: '/provider/setup-profile', cta: 'Add photos' },
            { key: 'avail', label: 'Set your availability', done: hasAvail === true,
              hint: 'So customers only book dates you can do', action: () => setTab('availability'), cta: 'Open calendar' },
            { key: 'approved', label: 'Get approved', done: profile.status === 'approved',
              hint: 'We review new vendors quickly — no action needed' },
            { key: 'payouts', label: 'Connect payouts', done: payouts?.payouts_enabled === true,
              hint: 'Link your bank so booking payments reach you automatically',
              action: setupPayouts, cta: payoutsBusy ? 'Opening…' : payouts?.connected ? 'Finish setup' : 'Set up payouts' },
          ];
          const doneCount = steps.filter(s => s.done).length;
          if (doneCount === steps.length) return null;
          return (
            <div className="bg-card border border-border/70 rounded-2xl shadow-soft p-5 mb-8 fade-up" data-testid="setup-checklist">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-foreground">Get set up to take bookings</h2>
                <span className="text-xs text-muted-foreground" data-testid="checklist-progress">{doneCount} of {steps.length} done</span>
              </div>
              <div className="h-1.5 bg-secondary rounded-full mb-5 overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${(doneCount / steps.length) * 100}%` }} />
              </div>
              <ul className="space-y-3">
                {steps.map(s => (
                  <li key={s.key} className="flex items-center gap-3" data-testid={`checklist-${s.key}`}>
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${s.done ? 'bg-emerald-500/15 text-emerald-600' : 'bg-secondary text-muted-foreground'}`}>
                      {s.done ? <Check size={11} weight="bold" /> : <span className="w-1.5 h-1.5 rounded-full bg-current" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${s.done ? 'text-muted-foreground line-through' : 'text-foreground font-medium'}`}>{s.label}</p>
                      {!s.done && <p className="text-[11px] text-muted-foreground">{s.hint}</p>}
                    </div>
                    {!s.done && s.cta && (s.link
                      ? <Link to={s.link} className="text-xs font-semibold text-primary bg-secondary hover:bg-primary hover:text-primary-foreground rounded-lg px-3 py-2 btn-anim no-underline shrink-0">{s.cta}</Link>
                      : <button onClick={s.action} className="text-xs font-semibold text-primary bg-secondary hover:bg-primary hover:text-primary-foreground rounded-lg px-3 py-2 btn-anim cursor-pointer shrink-0">{s.cta}</button>)}
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}

        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight text-foreground">Provider Dashboard</h1>
            <p className="text-muted-foreground mt-1">{profile?.business_name}</p>
            {profile?.status === 'pending' && (
              <div className="mt-2 inline-flex items-center gap-2 bg-amber-500/10 text-amber-700 rounded-full px-3 py-1 text-xs font-medium">
                <Clock size={12} /> Awaiting admin approval
              </div>
            )}
          </div>
          <Link to="/provider/setup-profile" className="inline-flex items-center border border-border bg-card rounded-xl px-4 h-11 text-sm font-medium text-foreground hover:bg-secondary/60 btn-anim shrink-0" data-testid="edit-profile-link">Edit Profile</Link>
        </div>

        {/* Featured placement */}
        {profile?.featured ? (
          <div className="flex items-center gap-3 bg-accent-coral/10 border border-accent-coral/30 rounded-2xl p-4 mb-8 fade-up" data-testid="featured-active">
            <Star size={20} weight="fill" className="text-accent-coral shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">You're featured!</p>
              <p className="text-xs text-muted-foreground">
                {profile.featured_cancel_at_period_end
                  ? `Your placement stays active${profile.featured_until ? ` until ${profile.featured_until}` : ''}, then won't renew.`
                  : `Your services show at the top of search results${profile.featured_until ? ` until ${profile.featured_until}` : ''} — renews automatically at $20/month.`}
              </p>
            </div>
            {!profile.featured_cancel_at_period_end && (
              <button onClick={cancelFeatured} disabled={featuredBusy}
                className="border border-border bg-card text-muted-foreground rounded-xl px-4 h-10 text-xs font-medium btn-anim hover:bg-secondary/60 hover:text-foreground disabled:opacity-50 cursor-pointer shrink-0"
                data-testid="cancel-featured-button">
                {featuredBusy ? 'Cancelling…' : 'Cancel'}
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-card border border-border/70 rounded-2xl shadow-soft p-4 mb-8 fade-up" data-testid="featured-upsell">
            <Star size={20} weight="fill" className="text-accent-coral shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">Get featured — $20/month</p>
              <p className="text-xs text-muted-foreground">Show your services at the top of search results with a Featured badge. Cancel anytime.</p>
            </div>
            <button onClick={buyFeatured} disabled={featuredBusy}
              className="bg-accent-coral text-primary-foreground rounded-xl px-5 h-10 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-accent-coral/90 disabled:opacity-50 cursor-pointer shrink-0"
              data-testid="get-featured-button">
              {featuredBusy ? 'Redirecting…' : 'Get featured'}
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {[
            { label: 'Profile views', value: profileViews.toLocaleString(), icon: <Eye size={20} />,
              hint: profileViews === 0 ? 'Add photos to get discovered' : 'People who viewed your profile' },
            { label: 'Upcoming payout', value: `$${upcomingPayout.toFixed(0)}`, icon: <Wallet size={20} />,
              hint: 'Booked & paid — releases when complete' },
            { label: 'Total earnings', value: `$${totalEarnings.toFixed(0)}`, icon: <CurrencyDollar size={20} /> },
            { label: 'Pending', value: pendingBookings.length, icon: <Clock size={20} /> },
            { label: 'Completed', value: completedBookings.length, icon: <Check size={20} /> },
            { label: 'Rating', value: profile?.rating_avg || '0', icon: <Star size={20} weight="fill" /> },
          ].map((stat, i) => (
            <div key={stat.label} className="bg-card rounded-2xl border border-border/70 shadow-soft p-5 fade-up" style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}>
              <div className="flex items-center gap-2 mb-2"><span className="text-primary">{stat.icon}</span><span className="text-sm text-muted-foreground">{stat.label}</span></div>
              <div className="text-3xl font-display font-semibold text-foreground tabular-nums">{stat.value}</div>
              {stat.hint && <p className="text-[11px] text-muted-foreground mt-1 leading-tight">{stat.hint}</p>}
            </div>
          ))}
        </div>

        {/* Grow your bookings — share your page + collect reviews. Only useful once
            the vendor is approved and has a public URL. */}
        {profile?.status === 'approved' && publicUrl && (
          <div className="bg-card border border-border/70 rounded-2xl shadow-soft p-5 mb-8 fade-up" data-testid="share-card">
            <div className="flex items-start gap-3 mb-4">
              <span className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 text-primary"><ShareNetwork size={18} weight="fill" /></span>
              <div>
                <h2 className="text-sm font-semibold text-foreground">Grow your bookings</h2>
                <p className="text-xs text-muted-foreground">Share your Bookr page to bring in your own clients — every booking runs through Bookr with protected payments.</p>
              </div>
            </div>

            {/* Copyable public link */}
            <div className="flex items-center gap-2 bg-secondary/50 border border-border/60 rounded-xl px-3 py-2 mb-3">
              <LinkSimple size={15} className="text-muted-foreground shrink-0" />
              <span className="flex-1 min-w-0 truncate text-xs text-foreground" data-testid="share-url">{publicUrl}</span>
              <button onClick={() => copyLink(publicUrl, 'Profile link copied')}
                className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-primary bg-card border border-border rounded-lg px-2.5 py-1.5 btn-anim hover:bg-primary hover:text-primary-foreground cursor-pointer"
                data-testid="copy-link-button">
                <Copy size={13} /> Copy
              </button>
            </div>

            {/* Share actions */}
            <div className="flex flex-wrap gap-2">
              <button onClick={nativeShare}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-foreground bg-primary rounded-lg px-3 py-2 btn-anim shadow-soft hover:bg-primary/90 cursor-pointer"
                data-testid="share-button">
                <ShareNetwork size={14} weight="fill" /> Share my page
              </button>
              <a href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${publicUrl}`)}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground bg-secondary rounded-lg px-3 py-2 btn-anim hover:bg-secondary/70 no-underline cursor-pointer">
                WhatsApp
              </a>
              <a href={`mailto:?subject=${encodeURIComponent('Book me on Bookr')}&body=${encodeURIComponent(`${shareText}\n\n${publicUrl}`)}`}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground bg-secondary rounded-lg px-3 py-2 btn-anim hover:bg-secondary/70 no-underline cursor-pointer">
                Email
              </a>
              <button onClick={() => copyLink(reviewText, 'Review request copied — send it to a past client')}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground bg-secondary rounded-lg px-3 py-2 btn-anim hover:bg-secondary/70 cursor-pointer"
                data-testid="copy-review-request">
                <Star size={13} weight="fill" className="text-amber-400" /> Copy a review request
              </button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-3">More reviews rank you higher in search and the AI planner — a quick ask to happy past clients goes a long way.</p>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto">
          {['bookings', 'quotes', 'confirmed', 'completed', 'availability', 'verification'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors capitalize cursor-pointer ${tab === t ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              data-testid={`tab-${t}`}>
              {t === 'bookings' ? 'Pending' : t === 'verification' ? 'ID Verify'
                : t === 'quotes' ? `Quotes${quotes.filter(q => q.status === 'requested').length ? ` (${quotes.filter(q => q.status === 'requested').length})` : ''}` : t}
            </button>
          ))}
        </div>

        {['bookings', 'confirmed', 'completed'].includes(tab) && (loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-secondary/70 rounded-xl animate-pulse" />)}</div>
        ) : (
          <div className="space-y-3" data-testid="provider-bookings-list">
            {(tab === 'bookings' ? pendingBookings : tab === 'confirmed' ? confirmedBookings : completedBookings).length === 0 ? (
              <div className="flex flex-col items-center text-center py-16 bg-card rounded-2xl border border-border/70 shadow-soft fade-up">
                <div className="bg-secondary rounded-full p-4 mb-4">
                  <CalendarBlank size={28} className="text-primary" />
                </div>
                <p className="text-sm text-muted-foreground mb-5">No {tab} bookings yet — keep your calendar open and they'll come.</p>
                <button onClick={() => setTab('availability')} className="inline-flex items-center bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer">Manage availability</button>
              </div>
            ) : (
              (tab === 'bookings' ? pendingBookings : tab === 'confirmed' ? confirmedBookings : completedBookings).map((booking, i) => (
                <div key={booking.booking_id} className="bg-card border border-border/70 rounded-xl shadow-soft p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-secondary/50 transition-colors fade-up" style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }} data-testid={`provider-booking-${booking.booking_id}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="flex items-center gap-1 text-sm font-medium text-foreground"><User size={14} className="text-primary" /> {booking.customer?.name || 'Customer'}</span>
                      <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${getStatusColor(booking.status)}`}>{booking.status}</span>
                    </div>
                    <p className="text-sm font-medium text-foreground">{booking.service?.title || 'Service'}</p>
                    <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                      <span><CalendarBlank size={12} className="inline mr-1" />{booking.booking_date}</span>
                      <span><Clock size={12} className="inline mr-1" />{booking.start_time}</span>
                      {booking.location_address && <span>{booking.location_address}</span>}
                    </div>
                    {(booking.customer?.email || booking.customer?.phone) && (
                      <div className="flex items-center gap-3 mt-1.5 text-[11px] text-primary" data-testid={`customer-contact-${booking.booking_id}`}>
                        {booking.customer.email && <span className="inline-flex items-center gap-1"><EnvelopeSimple size={12} /> {booking.customer.email}</span>}
                        {booking.customer.phone && <span className="inline-flex items-center gap-1"><Phone size={12} /> {booking.customer.phone}</span>}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-xl font-display font-semibold text-foreground">${booking.provider_payout}</div>
                      <span className="text-xs text-muted-foreground">payout</span>
                    </div>
                    {booking.status === 'pending' && (
                      <div className="flex gap-2">
                        <button onClick={() => updateStatus(booking.booking_id, 'confirmed')} className="bg-primary text-primary-foreground h-11 w-11 flex items-center justify-center rounded-xl btn-anim shadow-soft hover:bg-primary/90 cursor-pointer" aria-label="Accept booking" data-testid={`accept-${booking.booking_id}`}><Check size={16} /></button>
                        <button onClick={() => updateStatus(booking.booking_id, 'cancelled')} className="border border-border bg-card h-11 w-11 flex items-center justify-center rounded-xl text-muted-foreground hover:bg-secondary/60 btn-anim cursor-pointer" aria-label="Decline booking" data-testid={`decline-${booking.booking_id}`}><X size={16} /></button>
                      </div>
                    )}
                    {booking.status === 'confirmed' && (
                      <button onClick={() => updateStatus(booking.booking_id, 'completed')} className="bg-primary text-primary-foreground rounded-xl px-4 h-11 text-xs font-semibold btn-anim shadow-soft hover:bg-primary/90 cursor-pointer" data-testid={`complete-${booking.booking_id}`}>Mark Complete</button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ))}

        {/* Quote requests */}
        {tab === 'quotes' && (
          <QuotesPanel quotes={quotes} headers={headers} onChanged={fetchData} />
        )}

        {/* Availability Calendar */}
        {tab === 'availability' && (
          <div className="grid md:grid-cols-[1fr_300px] gap-6" data-testid="availability-tab">
            <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 fade-up">
              {/* Month nav */}
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-semibold text-foreground">Manage Availability</h3>
                <div className="flex items-center gap-2">
                  <button onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1))} className="h-11 w-11 flex items-center justify-center hover:bg-secondary rounded-xl transition-colors cursor-pointer" aria-label="Previous month"><CaretLeft size={16} className="text-muted-foreground" /></button>
                  <span className="text-sm font-medium text-foreground min-w-[140px] text-center">{calMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
                  <button onClick={() => setCalMonth(new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1))} className="h-11 w-11 flex items-center justify-center hover:bg-secondary rounded-xl transition-colors cursor-pointer" aria-label="Next month"><CaretRight size={16} className="text-muted-foreground" /></button>
                </div>
              </div>

              {/* Day grid */}
              <div className="grid grid-cols-7 gap-1">
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
                  <div key={d} className="text-[10px] font-semibold text-muted-foreground text-center py-2">{d}</div>
                ))}
                {Array.from({ length: new Date(calMonth.getFullYear(), calMonth.getMonth(), 1).getDay() }).map((_, i) => <div key={`e${i}`} />)}
                {Array.from({ length: new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 0).getDate() }).map((_, i) => {
                  const day = i + 1;
                  const dateStr = `${calMonth.getFullYear()}-${String(calMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const today = new Date(); today.setHours(0,0,0,0);
                  const isPast = new Date(calMonth.getFullYear(), calMonth.getMonth(), day) < today;
                  const slot = avail.slots.find(s => s.date === dateStr);
                  const isBooked = (avail.booked_dates || []).includes(dateStr);
                  const isAvailable = slot?.available === true;
                  const isBlocked = slot?.available === false;
                  const isSelected = selectedDate === dateStr;

                  let bg = 'bg-card text-foreground hover:bg-secondary cursor-pointer';
                  if (isPast) bg = 'bg-card text-muted-foreground/40 cursor-default';
                  else if (isBooked) bg = 'bg-amber-500/10 text-amber-700 font-medium cursor-pointer';
                  else if (isBlocked) bg = 'bg-red-500/10 text-red-500 line-through cursor-pointer';
                  else if (isAvailable) bg = 'bg-emerald-500/10 text-emerald-700 font-medium cursor-pointer';
                  if (isSelected && !isPast) bg += ' ring-2 ring-primary';

                  return (
                    <button key={day} disabled={isPast}
                      onClick={() => !isPast && setSelectedDate(isSelected ? null : dateStr)}
                      className={`py-3 text-xs rounded-lg transition-all ${bg}`}
                      data-testid={`cal-day-${dateStr}`}>
                      {day}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-5 mt-4 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500/15 rounded-full" /> Available</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-500/15 rounded-full" /> Booked</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500/15 rounded-full" /> Blocked</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-card ring-1 ring-border rounded-full" /> Open</span>
              </div>
            </div>

            {/* Right panel */}
            <div className="space-y-4">
              {/* Quick actions */}
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-5 fade-up" style={{ animationDelay: '50ms' }}>
                <h4 className="text-sm font-semibold text-foreground mb-3">Quick actions</h4>
                {selectedDate ? (
                  <div className="space-y-3">
                    <p className="text-sm text-foreground font-medium">{new Date(selectedDate + 'T12:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-[10px] font-medium text-muted-foreground mb-1">Start</label>
                        <input type="time" value={slotStart} onChange={e => setSlotStart(e.target.value)}
                          className="w-full h-11 rounded-xl border border-border bg-card px-2 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-muted-foreground mb-1">End</label>
                        <input type="time" value={slotEnd} onChange={e => setSlotEnd(e.target.value)}
                          className="w-full h-11 rounded-xl border border-border bg-card px-2 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => toggleDay(selectedDate)} className="flex-1 bg-emerald-600 text-white h-11 rounded-xl text-xs font-semibold btn-anim shadow-soft hover:bg-emerald-700 cursor-pointer" data-testid="set-available">
                        Set available
                      </button>
                      <button onClick={() => blockDay(selectedDate)} className="flex-1 bg-red-500/10 text-red-600 h-11 rounded-xl text-xs font-semibold btn-anim hover:bg-red-500/20 cursor-pointer" data-testid="block-day">
                        Block day
                      </button>
                    </div>
                    {avail.slots.find(s => s.date === selectedDate) && (
                      <button onClick={() => { axios.delete(`${API}/availability/${selectedDate}`, { headers }).then(fetchAvailability); }} className="w-full border border-border bg-card text-muted-foreground h-11 rounded-xl text-xs font-medium hover:bg-secondary/60 btn-anim cursor-pointer" data-testid="clear-day">
                        Clear setting
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground leading-relaxed">Click a date on the calendar to manage availability.</p>
                )}
              </div>

              {/* Upcoming booked dates */}
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-5 fade-up" style={{ animationDelay: '100ms' }}>
                <h4 className="text-sm font-semibold text-foreground mb-3">Upcoming bookings</h4>
                {(avail.booked_dates || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No bookings this month</p>
                ) : (
                  <div className="space-y-1.5">
                    {(avail.booked_dates || []).sort().map(d => (
                      <div key={d} className="flex items-center gap-2 text-xs rounded-xl px-2 py-1.5 -mx-2 hover:bg-secondary/50 transition-colors">
                        <span className="w-2 h-2 bg-amber-400 rounded-full shrink-0" />
                        <span className="text-foreground">{new Date(d + 'T12:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ID Verification tab */}
        {tab === 'verification' && (
          <div className="max-w-lg space-y-5" data-testid="verification-tab">
            <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 fade-up">
              <div className="flex items-center gap-3 mb-4">
                <IdentificationCard size={24} className="text-primary" />
                <div>
                  <h3 className="font-display text-lg font-semibold text-foreground">Identity Verification</h3>
                  <p className="text-xs text-muted-foreground">Upload a government-issued ID to get verified</p>
                </div>
              </div>

              {/* Current status */}
              {profile?.id_verification_status === 'verified' && (
                <div className="bg-emerald-500/10 rounded-xl p-3 mb-4 flex items-center gap-2" data-testid="verified-badge">
                  <ShieldCheck size={16} weight="fill" className="text-emerald-600" />
                  <span className="text-xs font-medium text-emerald-700">Identity verified</span>
                </div>
              )}
              {profile?.id_verification_status === 'pending' && (
                <div className="bg-amber-500/10 rounded-xl p-3 mb-4 flex items-center gap-2" data-testid="pending-badge">
                  <Clock size={16} className="text-amber-600" />
                  <span className="text-xs font-medium text-amber-700">Verification under review — usually within 24 hours</span>
                </div>
              )}
              {profile?.id_verification_status === 'rejected' && (
                <div className="bg-red-500/10 rounded-xl p-3 mb-4 flex items-center gap-2" data-testid="rejected-badge">
                  <WarningCircle size={16} className="text-red-600" />
                  <span className="text-xs font-medium text-red-700">Verification rejected — please re-upload a clearer photo</span>
                </div>
              )}

              {/* Upload form */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Document type</label>
                  <select id="doc-type" defaultValue="government_id" className="w-full h-11 rounded-xl border border-border bg-card px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-testid="doc-type-select">
                    <option value="government_id">Government-issued ID</option>
                    <option value="drivers_license">Driver's License</option>
                    <option value="passport">Passport</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1.5">Upload ID photo</label>
                  <input type="file" id="id-upload" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) { toast.error('File too large. Max 5MB'); return; }
                      setUploading(true);
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('document_type', document.getElementById('doc-type').value);
                        await axios.post(`${API}/verification/upload`, formData, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
                        // Refresh
                        const r = await axios.get(`${API}/verification/status`, { headers });
                        setVerifications(r.data);
                        fetchData();
                      } catch (err) { console.error('Upload error:', err); toast.error('Upload failed'); }
                      finally { setUploading(false); }
                    }}
                    data-testid="id-file-input" />
                  <button onClick={() => document.getElementById('id-upload').click()} disabled={uploading}
                    className="w-full border-2 border-dashed border-border rounded-2xl p-6 text-center hover:border-primary hover:bg-secondary/40 transition-all cursor-pointer disabled:opacity-50"
                    data-testid="upload-id-button">
                    <UploadSimple size={24} className="mx-auto mb-2 text-primary" />
                    <p className="text-sm font-medium text-foreground">{uploading ? 'Uploading...' : 'Click to upload your ID'}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">JPEG, PNG, WebP, or PDF. Max 5MB</p>
                  </button>
                </div>
              </div>

              {/* Requirements */}
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Requirements</p>
                <ul className="space-y-1.5 text-xs text-muted-foreground">
                  <li className="flex items-center gap-2"><Check size={12} className="text-emerald-500 shrink-0" /> Clear, readable photo of your full ID</li>
                  <li className="flex items-center gap-2"><Check size={12} className="text-emerald-500 shrink-0" /> Name must match your Bookr.ai profile</li>
                  <li className="flex items-center gap-2"><Check size={12} className="text-emerald-500 shrink-0" /> Not expired</li>
                  <li className="flex items-center gap-2"><Check size={12} className="text-emerald-500 shrink-0" /> No filters, screenshots, or cropped photos</li>
                </ul>
              </div>
            </div>

            {/* Past submissions */}
            {verifications.length > 0 && (
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-5 fade-up" style={{ animationDelay: '50ms' }} data-testid="verification-history">
                <h4 className="text-sm font-semibold text-foreground mb-3">Submission history</h4>
                <div className="space-y-2">
                  {verifications.map(v => (
                    <div key={v.verification_id} className="flex items-center justify-between py-2 px-2 -mx-2 rounded-xl border-b border-border/60 last:border-0 hover:bg-secondary/50 transition-colors">
                      <div className="flex items-center gap-2">
                        <IdentificationCard size={14} className="text-muted-foreground" />
                        <span className="text-xs text-foreground capitalize">{v.document_type.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground">{new Date(v.submitted_at).toLocaleDateString()}</span>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                          v.status === 'approved' ? 'bg-emerald-500/10 text-emerald-600' :
                          v.status === 'rejected' ? 'bg-red-500/10 text-red-600' :
                          'bg-amber-500/10 text-amber-600'
                        }`}>{v.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
